import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Button, ImageUpload } from '@/components';
import { SideBarModal } from '@/components/Modal';
import { SearchableDropdown } from '@/components/SearchableDropdown';
import { DatePicker } from '@/components/DatePicker';
import { getListPitstop, Pitstop } from '@/services/api/pitstops';
import { showErrorToast } from '@/components/toast/globalToast';
import { ReceiveItemInput, ReceiveItemRowError } from '@/types/purchase-order';
import { downloadCsv, parseCsv } from '@/utils';

dayjs.extend(customParseFormat);

interface ReceiveItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (items: ReceiveItemInput[]) => void;
  isSubmitting?: boolean;
  subtitle?: string;
  purchaseOrderCode?: string;
  // The ordered model is RTO, so every unit needs a contract duration — the
  // backend refuses the batch without one.
  requiresContractDuration?: boolean;
  // Per-row rejections from the last failed submit. The backend validates the
  // whole batch before writing, so these arrive together and map straight onto
  // the rows still on screen.
  submitErrors?: ReceiveItemRowError[];
}

interface RowData {
  // Identity that survives reordering: rows are added, removed and replaced
  // wholesale by a CSV import, and both the row's errors and its in-flight
  // photo upload have to keep pointing at the same unit through all of that.
  id: string;
  vin: string;
  platNomor: string;
  imei: string;
  color: string;
  receivedAt: string;
  contractStartDate: string;
  contractDuration: string;
  hubID: number;
  hubName: string;
  hubCityID: number;
  hubCityName: string;
  stnkLink: string;
  stnkAnnualExpiryDate: string;
  stnkFiveYearExpiryDate: string;
  vehicleImageURL: string;
}

type RowErrors = Partial<Record<keyof RowData, string>>;

const emptyRow = (): RowData => ({
  id: uuidv4(),
  vin: '',
  platNomor: '',
  imei: '',
  color: '',
  receivedAt: '',
  contractStartDate: '',
  contractDuration: '',
  hubID: 0,
  hubName: '',
  hubCityID: 0,
  hubCityName: '',
  stnkLink: '',
  stnkAnnualExpiryDate: '',
  stnkFiveYearExpiryDate: '',
  vehicleImageURL: '',
});

// Every field counts: a row carrying only an STNK link is still something ops
// typed or uploaded, and dropping it as "blank" loses their work silently.
const isBlankRow = (row: RowData) =>
  !row.vin.trim() &&
  !row.platNomor.trim() &&
  !row.imei.trim() &&
  !row.color.trim() &&
  !row.receivedAt &&
  !row.contractStartDate &&
  !row.contractDuration.trim() &&
  !row.hubID &&
  !row.stnkLink.trim() &&
  !row.stnkAnnualExpiryDate &&
  !row.stnkFiveYearExpiryDate &&
  !row.vehicleImageURL.trim();

const inputClass = (hasError?: boolean) =>
  `w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 ${
    hasError ? 'border-red-500' : 'border-gray-300'
  }`;

// --- Bulk upload template -------------------------------------------------

const TEMPLATE_HEADER = [
  'Received Date (YYYY-MM-DD HH:MM)',
  'VIN',
  'Plate Number (no spaces)',
  'IMEI',
  'Color',
  'Contract Start Date (YYYY-MM-DD)',
  'Contract Duration (months)',
  'Hub Name',
  'STNK Link (URL)',
  'STNK Annual Expiry Date (YYYY-MM-DD)',
  'STNK 5-Year Expiry Date (YYYY-MM-DD)',
  'Vehicle Image URL',
];

// Rows whose first cell starts with '#' are treated as comments/examples and
// skipped on upload, so the sample line can safely ship inside the template.
const COMMENT_PREFIX = '#';

// Accepted date inputs, in priority order. Anything else is left blank for the
// ops user to fix in the form.
const DATE_FORMATS = [
  'YYYY-MM-DD HH:mm',
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DDTHH:mm',
  'DD/MM/YYYY HH:mm',
  'DD/MM/YYYY HH:mm:ss',
  'DD-MM-YYYY HH:mm',
  'YYYY-MM-DD',
  'DD/MM/YYYY',
];

// Date-only columns (contract start, STNK expiries) never carry a time.
const DATE_ONLY_FORMATS = ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD-MM-YYYY'];

// Months, whole and at least one — the backend enforces the same floor, and a
// class-validator rejection comes back without the per-row detail this form
// needs, so a zero must never leave the browser.
const isValidContractDuration = (value: string) =>
  /^\d+$/.test(value) && Number(value) >= 1;

const normalizeHeader = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, '');

// Maps the template columns (and a few tolerated aliases) onto row fields.
// Order matters: the first matcher that accepts a header wins, so the more
// specific tests ('stnkannual', 'contractstart') sit before the looser ones.
const COLUMN_MATCHERS: { key: keyof RowData; test: (h: string) => boolean }[] = [
  {
    key: 'receivedAt',
    test: (h) => h.includes('received') || h.includes('tanggal'),
  },
  { key: 'vin', test: (h) => h.includes('vin') || h.includes('rangka') },
  { key: 'platNomor', test: (h) => h.includes('plate') || h.includes('plat') },
  { key: 'imei', test: (h) => h.includes('imei') || h.includes('mesin') },
  { key: 'color', test: (h) => h.includes('color') || h.includes('warna') },
  {
    key: 'contractStartDate',
    test: (h) => h.includes('contractstart') || h.includes('mulaikontrak'),
  },
  {
    key: 'contractDuration',
    test: (h) => h.includes('contractduration') || h.includes('durasi'),
  },
  { key: 'hubName', test: (h) => h.includes('hub') },
  {
    key: 'stnkAnnualExpiryDate',
    test: (h) => h.includes('stnkannual') || h.includes('stnktahunan'),
  },
  {
    key: 'stnkFiveYearExpiryDate',
    test: (h) => h.includes('stnk5year') || h.includes('stnkfiveyear'),
  },
  { key: 'stnkLink', test: (h) => h.includes('stnk') },
  {
    key: 'vehicleImageURL',
    test: (h) => h.includes('vehicleimage') || h.includes('fotounit'),
  },
];

// Shared by the import notes and the rejection list: same shape, different tone.
const IssueList: React.FC<{ issues: string[] }> = ({ issues }) => (
  <ul className="mt-2 list-disc space-y-0.5 pl-4">
    {issues.slice(0, 10).map((issue) => (
      <li key={issue}>{issue}</li>
    ))}
    {issues.length > 10 && <li>and {issues.length - 10} more…</li>}
  </ul>
);

const ReceiveItemsModal: React.FC<ReceiveItemsModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  subtitle,
  purchaseOrderCode,
  requiresContractDuration = false,
  submitErrors,
}) => {
  const [rows, setRows] = useState<RowData[]>([emptyRow()]);
  // Keyed by row id, not position — see RowData.id.
  const [errors, setErrors] = useState<Record<string, RowErrors>>({});
  const [hubs, setHubs] = useState<Pitstop[]>([]);
  const [loadingHubs, setLoadingHubs] = useState(false);
  const [importIssues, setImportIssues] = useState<string[]>([]);
  const [importSummary, setImportSummary] = useState('');
  // The rejection list describes the rows as submitted, so it is only worth
  // showing until ops starts changing them.
  const [editedSinceSubmit, setEditedSinceSubmit] = useState(false);
  // Row ids with a photo upload still in flight — saving now would drop the
  // image, so the footer button waits for them.
  const [uploadingRows, setUploadingRows] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Read by the rejection effect below, which must not re-run when a row
  // changes: re-applying rejections would fight the fix ops is typing.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  // Kept apart from the import notes above: one reports what a CSV brought in,
  // the other what the server refused. Sharing one channel made a stale
  // rejection read as an import problem.
  const serverIssues = useMemo(
    () =>
      editedSinceSubmit || !submitErrors
        ? []
        : submitErrors.map(
            ({ index, vin, message }) =>
              `Unit #${index + 1} (${vin}): ${message}`
          ),
    [submitErrors, editedSinceSubmit]
  );

  const fetchHubs = useCallback(async () => {
    try {
      setLoadingHubs(true);
      const response = await getListPitstop({
        page: '1',
        size: '10000',
        type: 'HUB',
        hasStaff: true,
      });
      setHubs(response.data || []);
    } catch (error) {
      showErrorToast('Error', 'Failed to load hub locations');
      setHubs([]);
    } finally {
      setLoadingHubs(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setRows([emptyRow()]);
      setErrors({});
      setImportIssues([]);
      setImportSummary('');
      setEditedSinceSubmit(false);
      setUploadingRows(new Set());
      fetchHubs();
    }
  }, [isOpen, fetchHubs]);

  // A rejected submit leaves the batch on screen untouched; only the markers
  // change, so ops fixes the named rows instead of re-uploading everything.
  useEffect(() => {
    if (!submitErrors || submitErrors.length === 0) return;

    setEditedSinceSubmit(false);
    // The backend numbers its rejections by position in the batch it was sent,
    // which is the order the rows are still in.
    setErrors((prev) => {
      const next = { ...prev };
      // Every field the backend can reject is also a field on the row, so the
      // name maps straight through.
      submitErrors.forEach(({ index, field, message }) => {
        const rowID = rowsRef.current[index]?.id;
        if (rowID) next[rowID] = { ...next[rowID], [field]: message };
      });
      return next;
    });
  }, [submitErrors]);

  const hubIdOf = (hub: Pitstop) =>
    hub.id?.toString() || hub.ID?.toString() || '';

  const hubFields = (hub?: Pitstop) => ({
    hubID: Number(hub ? hubIdOf(hub) : 0) || 0,
    hubName: hub?.name || '',
    hubCityID: hub?.city?.id || hub?.cityID || 0,
    hubCityName: hub?.city?.name || hub?.cityName || '',
  });

  const updateRow = (rowID: string, patch: Partial<RowData>) => {
    setEditedSinceSubmit(true);
    setRows((prev) =>
      prev.map((row) => (row.id === rowID ? { ...row, ...patch } : row))
    );
    setErrors((prev) => ({
      ...prev,
      [rowID]: Object.keys(patch).reduce(
        (acc, key) => ({ ...acc, [key]: undefined }),
        { ...prev[rowID] }
      ),
    }));
  };

  const setRowUploading = (rowID: string, uploading: boolean) =>
    setUploadingRows((prev) => {
      const next = new Set(prev);
      if (uploading) next.add(rowID);
      else next.delete(rowID);
      return next;
    });

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const removeRow = (rowID: string) => {
    setRows((prev) => prev.filter((row) => row.id !== rowID));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[rowID];
      return next;
    });
    setRowUploading(rowID, false);
  };

  // Back to the initial state: a single empty unit, no errors, no import notes.
  const resetRows = () => {
    setRows([emptyRow()]);
    setErrors({});
    setImportIssues([]);
    setImportSummary('');
    setEditedSinceSubmit(true);
    setUploadingRows(new Set());
  };

  // Nothing to clear when the form is still just one untouched unit.
  const hasAnyInput = rows.length > 1 || rows.some((row) => !isBlankRow(row));

  // Header + one commented example line, pre-filled with a real hub name so
  // ops can see exactly what the Hub Name column has to match.
  const handleDownloadTemplate = () => {
    const sampleHub = hubs[0]?.name || 'Hub Name (must match FMS hub)';
    downloadCsv(
      `receive-units-template${purchaseOrderCode ? `-${purchaseOrderCode}` : ''}.csv`,
      [
        TEMPLATE_HEADER,
        [
          `${COMMENT_PREFIX}${dayjs().format('YYYY-MM-DD HH:mm')}`,
          'VIN12345678901234',
          'B1234XYZ',
          '860000000000001',
          'Black',
          '2026-01-31',
          '36',
          sampleHub,
          'https://example.com/stnk.pdf',
          '2027-01-31',
          '2031-01-31',
          'https://example.com/unit.jpg',
        ],
      ]
    );
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Allow re-uploading the same file after a fix.
    e.target.value = '';
    if (!file) return;

    if (!/\.csv$/i.test(file.name)) {
      showErrorToast('Error', 'File must be a .csv — use Download Template');
      return;
    }

    try {
      const matrix = parseCsv(await file.text());
      if (matrix.length === 0) {
        showErrorToast('Error', 'The CSV file is empty');
        return;
      }

      // Resolve the column order from the header row. Each column is claimed by
      // at most one field, so 'STNK Link' cannot also swallow the STNK expiry
      // columns that appear before it.
      const header = matrix[0].map(normalizeHeader);
      const columnIndex = {} as Record<keyof RowData, number>;
      const claimed = new Set<number>();
      COLUMN_MATCHERS.forEach(({ key, test }) => {
        const found = header.findIndex(
          (value, index) => !claimed.has(index) && test(value)
        );
        columnIndex[key] = found;
        if (found >= 0) claimed.add(found);
      });

      if (columnIndex.vin < 0) {
        showErrorToast(
          'Error',
          'CSV header not recognised — use Download Template and keep the header row'
        );
        return;
      }

      const hubByName = new Map<string, Pitstop>();
      hubs.forEach((hub) => {
        if (hub.name) hubByName.set(hub.name.trim().toLowerCase(), hub);
      });

      const cellAt = (row: string[], key: keyof RowData) => {
        const index = columnIndex[key];
        return index >= 0 ? (row[index] ?? '').trim() : '';
      };

      const imported: RowData[] = [];
      const issues: string[] = [];
      let skipped = 0;

      matrix.slice(1).forEach((cells, i) => {
        const lineNumber = i + 2; // 1-based, header is line 1
        if ((cells[0] || '').startsWith(COMMENT_PREFIX)) {
          skipped += 1;
          return;
        }

        const rawDate = cellAt(cells, 'receivedAt');
        const parsedDate = rawDate ? dayjs(rawDate, DATE_FORMATS, true) : null;
        if (rawDate && !parsedDate?.isValid()) {
          issues.push(
            `Line ${lineNumber}: date "${rawDate}" is not in YYYY-MM-DD HH:MM format`
          );
        }

        // Date-only columns are all optional, so an unparseable value is
        // reported and dropped rather than blocking the import.
        const dateOnly = (key: keyof RowData, label: string) => {
          const raw = cellAt(cells, key);
          if (!raw) return '';
          const parsed = dayjs(raw, DATE_ONLY_FORMATS, true);
          if (!parsed.isValid()) {
            issues.push(
              `Line ${lineNumber}: ${label} "${raw}" is not in YYYY-MM-DD format`
            );
            return '';
          }
          return parsed.format('YYYY-MM-DD');
        };

        const rawHub = cellAt(cells, 'hubName');
        const hub = rawHub ? hubByName.get(rawHub.toLowerCase()) : undefined;
        if (rawHub && !hub) {
          issues.push(`Line ${lineNumber}: hub "${rawHub}" not found in FMS`);
        }

        const vin = cellAt(cells, 'vin');
        if (!vin) issues.push(`Line ${lineNumber}: VIN is empty`);

        // Plate and colour are as mandatory as the VIN — reporting them here
        // rather than at submit is the whole point of reviewing an import.
        const platNomor = cellAt(cells, 'platNomor').replace(/\s+/g, '');
        if (!platNomor) {
          issues.push(`Line ${lineNumber}: plate number is empty`);
        }

        const color = cellAt(cells, 'color');
        if (!color) issues.push(`Line ${lineNumber}: color is empty`);

        const rawDuration = cellAt(cells, 'contractDuration');
        const durationIsValid = isValidContractDuration(rawDuration);
        if (rawDuration && !durationIsValid) {
          issues.push(
            `Line ${lineNumber}: contract duration "${rawDuration}" must be a whole number of months, at least 1`
          );
        }

        imported.push({
          id: uuidv4(),
          vin,
          // Plate numbers are stored space-free — stripped above.
          platNomor,
          imei: cellAt(cells, 'imei'),
          color,
          receivedAt: parsedDate?.isValid()
            ? parsedDate.format('YYYY-MM-DDTHH:mm')
            : '',
          contractStartDate: dateOnly(
            'contractStartDate',
            'contract start date'
          ),
          contractDuration: durationIsValid ? rawDuration : '',
          ...hubFields(hub),
          stnkLink: cellAt(cells, 'stnkLink'),
          stnkAnnualExpiryDate: dateOnly(
            'stnkAnnualExpiryDate',
            'STNK annual expiry date'
          ),
          stnkFiveYearExpiryDate: dateOnly(
            'stnkFiveYearExpiryDate',
            'STNK 5-year expiry date'
          ),
          vehicleImageURL: cellAt(cells, 'vehicleImageURL'),
        });
      });

      if (imported.length === 0) {
        showErrorToast('Error', 'No data rows found in the CSV file');
        return;
      }

      // Drop untouched starter rows, keep anything already filled in, then
      // append the imported units. A row whose photo is still uploading counts
      // as kept even while it looks blank — its URL only arrives when the
      // upload finishes, and dropping it would strand that upload.
      const kept = rows.filter(
        (row) => !isBlankRow(row) || uploadingRows.has(row.id)
      );
      setRows([...kept, ...imported]);

      setImportIssues(issues);
      setImportSummary(
        `${imported.length} unit${imported.length > 1 ? 's' : ''} imported from ${file.name}` +
          (skipped ? ` · ${skipped} example row skipped` : '') +
          (issues.length ? ` · ${issues.length} field(s) need attention` : '')
      );
    } catch (error) {
      showErrorToast('Error', 'Failed to read the CSV file');
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, RowErrors> = {};
    rows.forEach((row) => {
      const rowErrors: RowErrors = {};
      if (!row.receivedAt) rowErrors.receivedAt = 'Required';
      if (!row.vin.trim()) rowErrors.vin = 'Required';
      // Both are NOT NULL on the vehicle this receive creates.
      if (!row.platNomor.trim()) rowErrors.platNomor = 'Required';
      if (!row.color.trim()) rowErrors.color = 'Required';
      if (!row.hubID) rowErrors.hubID = 'Required';

      const duration = row.contractDuration.trim();
      if (requiresContractDuration && !duration) {
        rowErrors.contractDuration = 'Required for RTO models';
      } else if (duration && !isValidContractDuration(duration)) {
        rowErrors.contractDuration = 'Whole months, at least 1';
      }
      newErrors[row.id] = rowErrors;
    });

    setErrors(newErrors);
    return Object.values(newErrors).every(
      (rowErrors) => Object.keys(rowErrors).length === 0
    );
  };

  const handleSubmit = () => {
    if (!validate()) {
      showErrorToast(
        'Error',
        'Some units are incomplete — check the highlighted fields'
      );
      return;
    }

    const items: ReceiveItemInput[] = rows.map((row) => ({
      vin: row.vin.trim(),
      platNomor: row.platNomor.replace(/\s+/g, ''),
      imei: row.imei.trim() || undefined,
      color: row.color.trim(),
      receivedAt: new Date(row.receivedAt).toISOString(),
      contractStartDate: row.contractStartDate || undefined,
      contractDuration: row.contractDuration.trim()
        ? Number(row.contractDuration)
        : undefined,
      hubID: row.hubID,
      hubName: row.hubName,
      hubCityID: row.hubCityID,
      hubCityName: row.hubCityName,
      stnkLink: row.stnkLink.trim() || undefined,
      stnkAnnualExpiryDate: row.stnkAnnualExpiryDate || undefined,
      stnkFiveYearExpiryDate: row.stnkFiveYearExpiryDate || undefined,
      vehicleImageURL: row.vehicleImageURL.trim() || undefined,
    }));

    onSubmit(items);
  };

  // One photo still on its way is enough to hold the save: submitting now
  // would store the batch without it.
  const isUploadingPhoto = uploadingRows.size > 0;

  const hubOptions = hubs.map((hub) => ({
    value: hubIdOf(hub),
    label: hub.name || '',
  }));

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={onClose}
      position="right"
      width="lg"
      header={
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Record Received Units
          </h2>
          {subtitle && (
            <p className="text-sm font-normal text-gray-600 mt-1">{subtitle}</p>
          )}
        </div>
      }
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button variant="white" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || isUploadingPhoto}
          >
            {isSubmitting
              ? 'Saving...'
              : isUploadingPhoto
                ? 'Uploading photo...'
                : `Receive (${rows.length} unit${rows.length > 1 ? 's' : ''})`}
          </Button>
        </div>
      }
    >
      <div className="px-6 py-4 space-y-4">
        {/* Bulk upload — for batches too large to type in one by one */}
        <div className="rounded-lg border border-purple-100 bg-purple-50/50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold text-gray-800">
              Receiving many units at once?
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleDownloadTemplate}
                className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
              >
                <i className="ri-download-2-line text-base" />
                Download Template
              </button>
              <button
                type="button"
                onClick={handleUploadClick}
                disabled={loadingHubs}
                className={`inline-flex items-center gap-1 rounded border px-3 py-1.5 text-xs font-semibold ${
                  loadingHubs
                    ? 'border-gray-200 text-gray-400'
                    : 'border-purple-200 bg-white text-primary hover:bg-purple-50'
                }`}
              >
                <i className="ri-upload-2-line text-base" />
                {loadingHubs ? 'Loading hubs...' : 'Upload CSV'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          </div>
          <div className="mt-2 space-y-2 text-xs text-gray-600">
            <p>
              Fill the template — one unit per row — then upload it here. Rows
              land in the form below so you can review and fix them before
              saving.
            </p>

            <div>
              <div className="mb-1 font-semibold text-gray-700">
                Keep the header row and these columns:
              </div>
              <div className="flex flex-wrap gap-1">
                {TEMPLATE_HEADER.map((column) => (
                  <code
                    key={column}
                    className="rounded border border-gray-200 bg-white px-1.5 py-0.5 font-mono text-[11px] text-gray-700"
                  >
                    {column}
                  </code>
                ))}
              </div>
            </div>

            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900">
              <i className="ri-error-warning-line mt-[1px] flex-shrink-0 text-sm" />
              <div className="space-y-1">
                <div>
                  <span className="font-semibold">
                    Any row starting with “#” is skipped.
                  </span>{' '}
                  The template ships with one “#” example row so you can see the
                  expected format — leave it or delete it, it is never imported.
                </div>
                <div>
                  <span className="font-semibold">Hub Name</span> must match a
                  hub in FMS exactly, otherwise the row imports without a hub
                  and you have to pick one below.
                </div>
                <div>
                  <span className="font-semibold">
                    Received Date, VIN, Plate Number, Color and Hub Name
                  </span>{' '}
                  are required
                  {requiresContractDuration
                    ? ', and Contract Duration too — this order is for an RTO model'
                    : ''}
                  . Contract Start Date defaults to the received date. The STNK
                  and photo columns take plain links and stay optional — the
                  unit photo can also be uploaded per unit in the form below.
                </div>
              </div>
            </div>
          </div>

          {importSummary && (
            <div className="mt-3 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700">
              <span className="font-medium">{importSummary}</span>
              {importIssues.length > 0 && (
                <div className="text-amber-700">
                  <IssueList issues={importIssues} />
                </div>
              )}
            </div>
          )}
        </div>

        {serverIssues.length > 0 && (
          <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900">
            <div className="font-semibold">
              {serverIssues.length} unit
              {serverIssues.length > 1 ? 's were' : ' was'} rejected — nothing
              was saved. Fix the highlighted fields and receive again.
            </div>
            <IssueList issues={serverIssues} />
          </div>
        )}

        {/* Batch header — always offers a way back to a single empty unit */}
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-medium text-gray-700">
            {rows.length} unit{rows.length > 1 ? 's' : ''} in this batch
          </div>
          <button
            type="button"
            onClick={resetRows}
            disabled={!hasAnyInput}
            className={`inline-flex items-center gap-1 text-xs font-semibold ${
              hasAnyInput
                ? 'text-red-500 hover:underline'
                : 'cursor-not-allowed text-gray-300'
            }`}
          >
            <i className="ri-delete-bin-line text-base" />
            Clear all
          </button>
        </div>

        {rows.map((row, index) => (
          <div
            key={row.id}
            className="rounded-lg border border-gray-200 p-4 shadow-sm"
          >
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-800">
                Unit #{index + 1}
              </div>
              {rows.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeRow(row.id)}
                  className="text-gray-400 hover:text-red-600"
                  aria-label="Remove unit"
                >
                  <i className="ri-delete-bin-line text-base" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date &amp; Time Received{' '}
                  <span className="text-red-500">*</span>
                </label>
                <DatePicker
                  value={row.receivedAt}
                  onChange={(value) => updateRow(row.id, { receivedAt: value })}
                  placeholder="Select received date and time"
                  showTimeSelect
                  error={!!errors[row.id]?.receivedAt}
                  className="w-full"
                />
                {errors[row.id]?.receivedAt && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors[row.id]?.receivedAt}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  No. Rangka (VIN) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={row.vin}
                  onChange={(e) => updateRow(row.id, { vin: e.target.value })}
                  placeholder="Vehicle VIN"
                  className={inputClass(!!errors[row.id]?.vin)}
                />
                {errors[row.id]?.vin && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors[row.id]?.vin}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plate Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={row.platNomor}
                  onChange={(e) =>
                    updateRow(row.id, { platNomor: e.target.value })
                  }
                  placeholder="e.g. B1234XYZ (no spaces)"
                  className={inputClass(!!errors[row.id]?.platNomor)}
                />
                {errors[row.id]?.platNomor && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors[row.id]?.platNomor}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  IMEI
                </label>
                <input
                  type="text"
                  value={row.imei}
                  onChange={(e) => updateRow(row.id, { imei: e.target.value })}
                  placeholder="Device IMEI"
                  className={inputClass(!!errors[row.id]?.imei)}
                />
                {errors[row.id]?.imei && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors[row.id]?.imei}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={row.color}
                  onChange={(e) => updateRow(row.id, { color: e.target.value })}
                  placeholder="e.g. Black"
                  className={inputClass(!!errors[row.id]?.color)}
                />
                {errors[row.id]?.color && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors[row.id]?.color}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hub Location <span className="text-red-500">*</span>
                </label>
                <SearchableDropdown
                  placeholder={
                    loadingHubs ? 'Loading hubs...' : 'Select hub location'
                  }
                  options={hubOptions}
                  value={row.hubID ? row.hubID.toString() : ''}
                  onChange={(value) => {
                    const selectedHub = hubs.find((h) => hubIdOf(h) === value);
                    updateRow(row.id, hubFields(selectedHub));
                  }}
                  disabled={loadingHubs}
                  error={!!errors[row.id]?.hubID}
                />
                {errors[row.id]?.hubID && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors[row.id]?.hubID}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contract Start Date
                </label>
                <DatePicker
                  value={row.contractStartDate}
                  onChange={(value) =>
                    updateRow(row.id, { contractStartDate: value })
                  }
                  placeholder="Defaults to received date"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contract Duration (months)
                  {requiresContractDuration && (
                    <span className="text-red-500"> *</span>
                  )}
                </label>
                <input
                  type="number"
                  min={1}
                  value={row.contractDuration}
                  onChange={(e) =>
                    updateRow(row.id, { contractDuration: e.target.value })
                  }
                  placeholder={requiresContractDuration ? 'e.g. 36' : 'Optional'}
                  className={inputClass(!!errors[row.id]?.contractDuration)}
                />
                {errors[row.id]?.contractDuration && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors[row.id]?.contractDuration}
                  </p>
                )}
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  STNK Link
                </label>
                <input
                  type="text"
                  value={row.stnkLink}
                  onChange={(e) =>
                    updateRow(row.id, { stnkLink: e.target.value })
                  }
                  placeholder="https://… (optional — upload the file from the unit page)"
                  className={inputClass()}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  STNK Annual Expiry Date
                </label>
                <DatePicker
                  value={row.stnkAnnualExpiryDate}
                  onChange={(value) =>
                    updateRow(row.id, { stnkAnnualExpiryDate: value })
                  }
                  placeholder="Optional"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  STNK 5-Year Expiry Date
                </label>
                <DatePicker
                  value={row.stnkFiveYearExpiryDate}
                  onChange={(value) =>
                    updateRow(row.id, { stnkFiveYearExpiryDate: value })
                  }
                  placeholder="Optional"
                  className="w-full"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vehicle Image
                </label>
                <ImageUpload
                  value={row.vehicleImageURL}
                  onUploaded={(url) =>
                    updateRow(row.id, { vehicleImageURL: url })
                  }
                  onRemove={() => updateRow(row.id, { vehicleImageURL: '' })}
                  title="Add a photo of this unit"
                  onUploadingChange={(uploading) =>
                    setRowUploading(row.id, uploading)
                  }
                />
              </div>
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={addRow}
          className="inline-flex items-center gap-1 rounded border border-purple-200 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-purple-50"
        >
          <i className="ri-add-line text-base" />
          Add another unit
        </button>
      </div>
    </SideBarModal>
  );
};

export default ReceiveItemsModal;
